"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { useVotingModalState } from "@/components/voting/useVotingModalState";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
import WinnerDropBadge from "@/components/waves/drops/winner/WinnerDropBadge";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { ExtendedDrop, getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import Link from "next/link";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { WaveLeaderboardIdentity } from "../identity/WaveLeaderboardIdentity";
import WaveLeaderboardGalleryItemVotes from "./WaveLeaderboardGalleryItemVotes";
import ApprovalStatusBadge from "@/components/waves/approval/ApprovalStatusBadge";
import { isOfficiallyApprovedDrop } from "@/helpers/waves/approve-wave.helpers";
import { AdditionalActionPromiseBadge } from "@/components/waves/drops/AdditionalActionPromiseBadge";
interface WaveLeaderboardGalleryItemProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly artFocused?: boolean | undefined;
  readonly activeSort?: WaveDropsLeaderboardSort | undefined;
  readonly animationKey?: number | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
}
interface LeaderboardResultBadgeProps {
  readonly drop: ExtendedDrop;
  readonly isApproveDrop: boolean;
}
function LeaderboardResultBadge(t0) {
  const $ = _c(5);
  const {
    drop,
    isApproveDrop
  } = t0;
  if (isApproveDrop) {
    let t1;
    if ($[0] !== drop) {
      t1 = isOfficiallyApprovedDrop(drop) ? <ApprovalStatusBadge approvedAt={drop.winning_context?.decision_time ?? null} /> : null;
      $[0] = drop;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = drop.winning_context?.decision_time ?? null;
  let t2;
  if ($[2] !== drop.rank || $[3] !== t1) {
    t2 = <WinnerDropBadge rank={drop.rank} decisionTime={t1} />;
    $[2] = drop.rank;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export const WaveLeaderboardGalleryItem = memo<WaveLeaderboardGalleryItemProps>(t0 => {
  const $ = _c(85);
  const {
    drop,
    onDropClick,
    artFocused: t1,
    activeSort,
    animationKey: t2,
    isVotingClosed: t3,
    isVotingControlsLocked: t4,
    winningThreshold,
    winningThresholdMinDurationMs
  } = t0;
  const artFocused = t1 === undefined ? true : t1;
  const animationKey = t2 === undefined ? 0 : t2;
  const isVotingClosed = t3 === undefined ? false : t3;
  const isVotingControlsLocked = t4 === undefined ? false : t4;
  const isVotingActionLocked = isVotingClosed || isVotingControlsLocked;
  const {
    isOpen: isVoteModalOpen,
    open: openVoteModal,
    close: closeVoteModal
  } = useVotingModalState(isVotingActionLocked);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const isMobileScreen = useIsMobileScreen();
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const canShowVotingAction = canShowVote && !isVotingActionLocked;
  const primaryMedia = drop.parts[0]?.media[0];
  let t5;
  if ($[0] !== primaryMedia?.mime_type) {
    t5 = primaryMedia?.mime_type.startsWith("video/");
    $[0] = primaryMedia?.mime_type;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const isPrimaryMediaVideo = t5;
  const mediaImageScale = isTabletOrSmaller ? ImageScale.AUTOx450 : ImageScale.AUTOx1080;
  let t6;
  if ($[2] !== drop.metadata || $[3] !== isPrimaryMediaVideo) {
    t6 = isPrimaryMediaVideo ? null : getDropPreviewImageUrl(drop.metadata);
    $[2] = drop.metadata;
    $[3] = isPrimaryMediaVideo;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const previewImageUrl = t6;
  const isFirstRenderRef = useRef(true);
  const previousSortRef = useRef(activeSort);
  const timerRef = useRef(null);
  let t7;
  let t8;
  if ($[5] !== activeSort || $[6] !== animationKey || $[7] !== hasTouchScreen) {
    t7 = () => {
      if (hasTouchScreen) {
        return;
      }
      const sortChanged = previousSortRef.current !== activeSort;
      const shouldAnimate = isFirstRenderRef.current && animationKey > 0 || !isFirstRenderRef.current && sortChanged;
      isFirstRenderRef.current = false;
      previousSortRef.current = activeSort;
      if (!shouldAnimate) {
        return;
      }
      setIsHighlighting(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setIsHighlighting(false);
      }, 700);
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    };
    t8 = [activeSort, animationKey, hasTouchScreen];
    $[5] = activeSort;
    $[6] = animationKey;
    $[7] = hasTouchScreen;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  const userVote = drop.context_profile_context?.rating ?? 0;
  const hasUserVoted = userVote !== 0;
  const isNegativeVote = userVote < 0;
  const votingCreditType = drop.wave.voting_credit_type;
  const votingCreditLabel = WAVE_VOTING_LABELS[votingCreditType] ?? votingCreditType;
  const votePrefix = isNegativeVote ? "-" : "";
  let voteButtonLabel;
  if (hasUserVoted) {
    let t9;
    if ($[10] !== userVote) {
      t9 = formatNumberWithCommas(Math.abs(userVote));
      $[10] = userVote;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    voteButtonLabel = `You: ${votePrefix}${t9} ${votingCreditLabel}`;
  }
  const isApproveDrop = typeof winningThreshold === "number" && winningThreshold > 0;
  let t9;
  if ($[12] !== drop || $[13] !== isMobileScreen || $[14] !== onDropClick) {
    t9 = () => {
      startDropOpen({
        dropId: drop.id,
        waveId: drop.wave.id,
        source: "leaderboard_grid",
        isMobile: isMobileScreen
      });
      onDropClick(drop);
    };
    $[12] = drop;
    $[13] = isMobileScreen;
    $[14] = onDropClick;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleImageClick = t9;
  let t10;
  if ($[16] !== openVoteModal) {
    t10 = () => {
      openVoteModal();
    };
    $[16] = openVoteModal;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleVoteButtonClick = t10;
  const transitionClasses = hasTouchScreen ? "" : "tw-transition-all tw-duration-300 tw-ease-out";
  const groupClasses = artFocused ? `tw-group ${transitionClasses}` : "";
  const containerClass = `${groupClasses} tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-bg-iron-950/50 tw-border tw-border-solid tw-border-iron-800 tw-rounded-lg desktop-hover:hover:tw-border-iron-700 tw-shadow-lg desktop-hover:hover:tw-shadow-xl`;
  const highlightAnimation = isHighlighting && !hasTouchScreen ? "tw-animate-gallery-reveal" : "";
  const imageScaleClasses = hasTouchScreen ? "" : `tw-transform tw-duration-700 tw-ease-out group-hover/image:tw-scale-105 ${highlightAnimation}`;
  const t11 = `tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center ${imageScaleClasses}`;
  const t12 = primaryMedia?.mime_type ?? "image/jpeg";
  const t13 = primaryMedia?.url ?? "";
  let t14;
  if ($[18] !== mediaImageScale || $[19] !== previewImageUrl || $[20] !== t12 || $[21] !== t13) {
    t14 = <MediaDisplay media_mime_type={t12} media_url={t13} disableMediaInteraction={true} imageScale={mediaImageScale} previewImageUrl={previewImageUrl} />;
    $[18] = mediaImageScale;
    $[19] = previewImageUrl;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t11 || $[24] !== t14) {
    t15 = <div className={t11}>{t14}</div>;
    $[23] = t11;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== handleImageClick || $[27] !== t15) {
    t16 = <button className="tw-aspect-square tw-relative tw-flex-shrink-0 tw-cursor-pointer tw-touch-pan-y tw-overflow-hidden tw-bg-iron-900 tw-group/image tw-m-0 tw-w-full tw-overflow-hidden tw-rounded-lg tw-border-none tw-bg-transparent tw-p-0 tw-text-left" onClick={handleImageClick} type="button">{t15}</button>;
    $[26] = handleImageClick;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = primaryMedia?.mime_type;
  let t18;
  if ($[29] !== drop.id || $[30] !== t17) {
    t18 = <MediaTypeBadge mimeType={t17} dropId={drop.id} size="sm" />;
    $[29] = drop.id;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== drop.is_additional_action_promised) {
    t19 = drop.is_additional_action_promised === true && <AdditionalActionPromiseBadge />;
    $[32] = drop.is_additional_action_promised;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t18 || $[35] !== t19) {
    t20 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center tw-gap-1.5">{t18}{t19}</div>;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== drop || $[38] !== isApproveDrop) {
    t21 = <div className="tw-flex-shrink-0"><LeaderboardResultBadge drop={drop} isApproveDrop={isApproveDrop} /></div>;
    $[37] = drop;
    $[38] = isApproveDrop;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t20 || $[41] !== t21) {
    t22 = <div className="tw-flex tw-min-w-0 tw-items-start tw-justify-between tw-gap-2">{t20}{t21}</div>;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== drop.title) {
    t23 = drop.title && <h3 className="tw-mb-0 tw-mt-2 tw-min-w-0 tw-whitespace-normal tw-[overflow-wrap:anywhere] tw-break-words tw-text-sm tw-font-bold tw-leading-tight tw-text-iron-200">{drop.title}</h3>;
    $[43] = drop.title;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== drop.author) {
    t24 = drop.author?.handle && <UserProfileTooltipWrapper user={drop.author.handle ?? drop.author.id}><Link onClick={_temp} href={`/${drop.author?.handle}`} className="tw-mt-1 tw-block tw-max-w-full tw-truncate tw-text-xs tw-text-iron-400 tw-no-underline tw-transition-colors tw-duration-150 desktop-hover:hover:tw-text-iron-300 desktop-hover:hover:tw-underline">{drop.author?.handle}</Link></UserProfileTooltipWrapper>;
    $[45] = drop.author;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t22 || $[48] !== t23 || $[49] !== t24) {
    t25 = <div className="tw-mb-3 tw-min-w-0">{t22}{t23}{t24}</div>;
    $[47] = t22;
    $[48] = t23;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== drop) {
    t26 = <WaveLeaderboardIdentity drop={drop} variant="condensed" className="tw-mb-3" />;
    $[51] = drop;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  const t27 = artFocused ? "subtle" : "default";
  let t28;
  if ($[53] !== drop || $[54] !== isVotingClosed || $[55] !== t27 || $[56] !== winningThreshold || $[57] !== winningThresholdMinDurationMs) {
    t28 = <div className="tw-mb-3 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-justify-between tw-gap-y-2 tw-text-xs"><div className="tw-min-w-0 tw-flex-1"><WaveLeaderboardGalleryItemVotes drop={drop} variant={t27} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} /></div></div>;
    $[53] = drop;
    $[54] = isVotingClosed;
    $[55] = t27;
    $[56] = winningThreshold;
    $[57] = winningThresholdMinDurationMs;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== drop) {
    t29 = <div className="tw-flex tw-flex-shrink-0"><ParticipationDropVoteDetailsTrigger drop={drop} density="gallery" /></div>;
    $[59] = drop;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== artFocused || $[62] !== canShowVotingAction || $[63] !== drop || $[64] !== handleVoteButtonClick || $[65] !== voteButtonLabel) {
    t30 = canShowVotingAction && <div className="tw-ml-auto tw-flex tw-min-w-0 tw-flex-1 tw-justify-end"><VotingModalButton drop={drop} onClick={handleVoteButtonClick} variant={artFocused ? "subtle" : "default"} className="tw-box-border tw-h-8 tw-min-w-0 tw-max-w-full">{voteButtonLabel}</VotingModalButton></div>;
    $[61] = artFocused;
    $[62] = canShowVotingAction;
    $[63] = drop;
    $[64] = handleVoteButtonClick;
    $[65] = voteButtonLabel;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t29 || $[68] !== t30) {
    t31 = <div className="tw-mt-auto tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-3 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800/50 tw-pt-2">{t29}{t30}</div>;
    $[67] = t29;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t25 || $[71] !== t26 || $[72] !== t28 || $[73] !== t31) {
    t32 = <div className="tw-flex tw-flex-1 tw-flex-col tw-rounded-b-lg tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-bg-iron-950/50 tw-p-3">{t25}{t26}{t28}{t31}</div>;
    $[70] = t25;
    $[71] = t26;
    $[72] = t28;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== closeVoteModal || $[76] !== drop || $[77] !== isMobileScreen || $[78] !== isVoteModalOpen) {
    t33 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVoteModalOpen} onClose={closeVoteModal} /> : <VotingModal drop={drop} isOpen={isVoteModalOpen} onClose={closeVoteModal} />;
    $[75] = closeVoteModal;
    $[76] = drop;
    $[77] = isMobileScreen;
    $[78] = isVoteModalOpen;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== containerClass || $[81] !== t16 || $[82] !== t32 || $[83] !== t33) {
    t34 = <div className={containerClass}>{t16}{t32}{t33}</div>;
    $[80] = containerClass;
    $[81] = t16;
    $[82] = t32;
    $[83] = t33;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  return t34;
});
WaveLeaderboardGalleryItem.displayName = "WaveLeaderboardGalleryItem";
function _temp(e) {
  return e.stopPropagation();
}